/** DA delivery partner onboarding — local session + API helpers. */

export const DRIVER_PARTNER_TOKEN_KEY = "ttt_driver_partner_token";
export const DRIVER_PARTNER_TOKEN_MIRROR_KEY = "ttt_driver_partner_token_mirror";
export const DRIVER_PARTNER_PHONE_KEY = "ttt_driver_partner_phone";

function readTokenFromStorage(storage, key) {
  try {
    return (storage.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

function writeTokenToStorage(storage, key, token) {
  try {
    if (token) storage.setItem(key, token);
    else storage.removeItem(key);
  } catch {
    /* private mode / iOS quota */
  }
}

/** iOS PWAs can briefly fail localStorage reads on cold start — mirror in sessionStorage. */
export function loadDriverPartnerToken() {
  try {
    const primary = readTokenFromStorage(localStorage, DRIVER_PARTNER_TOKEN_KEY);
    if (primary) return primary;
    const mirror = readTokenFromStorage(sessionStorage, DRIVER_PARTNER_TOKEN_MIRROR_KEY);
    if (mirror) {
      writeTokenToStorage(localStorage, DRIVER_PARTNER_TOKEN_KEY, mirror);
      return mirror;
    }
  } catch {
    /* ignore */
  }
  return "";
}

/** Hub session — partner JWT only (not legacy dashboard URL tokens). */
export function readPartnerSessionToken() {
  return loadDriverPartnerToken();
}

export function hasPartnerSession() {
  return Boolean(readPartnerSessionToken());
}

export function saveDriverPartnerToken(token) {
  writeTokenToStorage(localStorage, DRIVER_PARTNER_TOKEN_KEY, token);
  writeTokenToStorage(sessionStorage, DRIVER_PARTNER_TOKEN_MIRROR_KEY, token);
}

export function loadSavedPartnerPhone() {
  try {
    return (localStorage.getItem(DRIVER_PARTNER_PHONE_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function savePartnerPhone(phone) {
  try {
    const clean = (phone || "").replace(/\D/g, "").slice(-10);
    if (clean) localStorage.setItem(DRIVER_PARTNER_PHONE_KEY, clean);
  } catch {
    /* ignore */
  }
}

export function isPartnerInviteQr(raw) {
  return Boolean(parsePartnerInviteFromText(raw));
}

export function partnerInvitePathFromScan(raw) {
  return parsePartnerInviteFromText(raw) || "/dd/join";
}

/** Full https URL to open on the phone after a successful partner QR scan. */
export function resolvePartnerInviteUrl(raw) {
  const text = (raw || "").trim();
  if (!text || !isPartnerInviteQr(text)) return "";
  if (text.startsWith("http://") || text.startsWith("https://")) {
    try {
      const u = new URL(text);
      return `${u.origin}${partnerInvitePathFromScan(text)}`;
    } catch {
      return "";
    }
  }
  const path = partnerInvitePathFromScan(text);
  return path ? `${window.location.origin}${path}` : "";
}

/** Navigate to the join link from a scanned QR (opens sign-up in this app). */
export function openPartnerInviteFromScan(raw) {
  const url = resolvePartnerInviteUrl(raw);
  if (!url) return false;
  window.location.assign(url);
  return true;
}

export function parsePartnerInviteFromText(raw) {
  const text = (raw || "").trim();
  if (!text) return "";
  const lower = text.toLowerCase();
  if (lower.includes("/dd/join") || lower.includes("/dd/scan")) return "/dd/join";
  try {
    const u = new URL(text, window.location.origin);
    const path = (u.pathname || "").toLowerCase();
    if (path.includes("/dd/join") || path.includes("/dd/scan")) return "/dd/join";
  } catch {
    /* plain text */
  }
  return "";
}

export async function fetchPartnerInviteInfo() {
  const res = await fetch("/api/driver/partner/invite");
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not load invite");
  return data;
}

function parsePartnerApiError(data, fallback) {
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.loc?.join(".") || JSON.stringify(d)).join("; ");
  }
  return fallback;
}

export async function registerDeliveryPartner({ name, phone, address, scooterNumber, upiId, photoFile, firebaseIdToken, payoutKind }) {
  const blob = photoFile instanceof Blob ? photoFile : photoFile?.blob;
  if (!blob) {
    throw new Error("Add your profile photo");
  }
  if (!firebaseIdToken) {
    throw new Error("Verify your mobile number with SMS first");
  }
  const ext =
    photoFile?.extension ||
    (blob.type === "image/webp" ? "webp" : blob.type === "image/png" ? "png" : "jpg");
  const fd = new FormData();
  fd.append("name", name);
  fd.append("phone", phone);
  fd.append("address", address);
  fd.append("scooter_number", scooterNumber);
  fd.append("upi_id", upiId);
  fd.append("payout_kind", payoutKind || "internal");
  fd.append("firebase_id_token", firebaseIdToken);
  fd.append("photo", blob, `partner.${ext}`);
  const res = await fetch("/api/driver/partner/register", { method: "POST", body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parsePartnerApiError(data, "Registration failed"));
  if (data.token) saveDriverPartnerToken(data.token);
  if (phone) savePartnerPhone(phone);
  return data;
}

export async function loginDeliveryPartnerFirebase(firebaseIdToken) {
  const fd = new FormData();
  fd.append("firebase_id_token", firebaseIdToken);
  const res = await fetch("/api/driver/partner/login-firebase", { method: "POST", body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parsePartnerApiError(data, "Could not log in"));
  if (data.token) saveDriverPartnerToken(data.token);
  const partnerPhone = data?.partner?.phone;
  if (partnerPhone) savePartnerPhone(partnerPhone);
  return data;
}

export function isMissingDeliveryPartnerAccountError(message) {
  return String(message || "").toLowerCase().includes("no delivery partner account");
}

/**
 * After SMS OTP — log in when the number already has a partner profile.
 * Only ask for sign-up details when no account exists yet.
 */
export async function loginOrNeedsPartnerSignup(firebaseIdToken) {
  try {
    const data = await loginDeliveryPartnerFirebase(firebaseIdToken);
    return { status: "logged_in", data };
  } catch (ex) {
    const message = ex?.message || String(ex);
    if (isMissingDeliveryPartnerAccountError(message)) {
      return { status: "needs_signup", message };
    }
    throw ex;
  }
}

export async function completePartnerRegistration(data) {
  return data;
}

/** Open Paytm / UPI to pay the delivery partner (manager pays partner's UPI). */
export function handlePartnerSignupBonus(signupBonus) {
  openPartnerPayout(signupBonus);
}

export function openPartnerPayout(payout) {
  const raw = (payout?.paytm_pay_url || payout?.paytm_url || payout?.upi_pay_url || "").trim();
  if (!raw) return false;
  const launch = raw.startsWith("paytmmp://") ? paytmLaunchUrl(raw) : raw;
  return launchUrl(launch);
}

export async function claimPartnerSignupBonus(token) {
  const fd = new FormData();
  fd.append("token", token);
  const res = await fetch("/api/driver/partner/claim-bonus", { method: "POST", body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not claim bonus");
  return data;
}

export async function fetchPartnerEarnings(token, { todayOnly = false } = {}) {
  const params = new URLSearchParams({ token });
  if (todayOnly) params.set("today_only", "true");
  const res = await fetch(`/api/driver/partner/earnings?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parsePartnerApiError(data, "Could not load earnings"));
  return data;
}

export async function fetchPartnerSurgeStatus(token) {
  const params = new URLSearchParams({ token });
  const res = await fetch(`/api/driver/partner/surge-status?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parsePartnerApiError(data, "Could not load surge status"));
  return data;
}

export async function lockInPartnerSurge(token) {
  const fd = new FormData();
  fd.append("token", token);
  const res = await fetch("/api/driver/partner/surge/lock-in", { method: "POST", body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parsePartnerApiError(data, "Could not lock in for surge"));
  return data;
}

export async function breakPartnerSurgeLock(token) {
  const fd = new FormData();
  fd.append("token", token);
  const res = await fetch("/api/driver/partner/surge/break-lock", { method: "POST", body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parsePartnerApiError(data, "Could not break surge lock"));
  return data;
}

function isAndroid() {
  return /android/i.test(typeof navigator !== "undefined" ? navigator.userAgent || "" : "");
}

function paytmLaunchUrl(url) {
  const raw = (url || "").trim();
  if (!raw.startsWith("paytmmp://")) return raw;
  if (!isAndroid()) return raw;
  const pathAndQuery = raw.slice("paytmmp://".length);
  return `intent://${pathAndQuery}#Intent;scheme=paytmmp;package=net.one97.paytm;end`;
}

function launchUrl(url) {
  if (!url) return false;
  try {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } catch {
    try {
      window.location.href = url;
      return true;
    } catch {
      return false;
    }
  }
}

/** @deprecated use openPartnerPayout */
export function openPartnerPaytmBonus(paytmUrl) {
  return openPartnerPayout({ paytm_pay_url: paytmUrl });
}
